import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import { format, parseISO } from "date-fns";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  tableContainer: {
    maxHeight: "500px",
    overflowY: "auto",
  },
  userInfo: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  totalRow: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: "bold",
  },
}));

const SalesDetailsModal = ({ open, onClose, userId, userName, startDate, endDate }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);

  useEffect(() => {
    if (open && userId) {
      fetchSalesDetails();
    }
  }, [open, userId, startDate, endDate]);

  const fetchSalesDetails = async () => {
    setLoading(true);
    try {
      const params = {};
      
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      const { data } = await api.get(`/sales/details/${userId}`, { params });
      setSalesData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), "dd/MM/yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const calculateTotal = () => {
    if (!salesData || !salesData.sales) return 0;
    return salesData.sales.reduce((sum, sale) => sum + parseFloat(sale.valor || 0), 0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        {i18n.t("users.salesReport.details.title")}
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : salesData ? (
          <>
            <Box className={classes.userInfo}>
              <Typography variant="h6" gutterBottom>
                {salesData.userName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("users.salesReport.salesGoal")}: {formatCurrency(salesData.salesGoal)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("users.salesReport.details.totalSales")}: {formatCurrency(calculateTotal())}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("users.salesReport.details.salesCount")}: {salesData.sales?.length || 0}
              </Typography>
            </Box>

            {salesData.sales && salesData.sales.length > 0 ? (
              <div className={classes.tableContainer}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        {i18n.t("users.salesReport.details.date")}
                      </TableCell>
                      <TableCell align="left">
                        {i18n.t("users.salesReport.details.clientName")}
                      </TableCell>
                      <TableCell align="center">
                        {i18n.t("users.salesReport.details.value")}
                      </TableCell>
                      <TableCell align="left">
                        {i18n.t("users.salesReport.details.observations")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.sales.map((sale) => (
                      <TableRow key={sale.id}>
                        <TableCell align="center">
                          {formatDate(sale.dataVenda)}
                        </TableCell>
                        <TableCell align="left">
                          {sale.NOMECLI || "-"}
                        </TableCell>
                        <TableCell align="center">
                          {formatCurrency(sale.valor)}
                        </TableCell>
                        <TableCell align="left">
                          {sale.observacoes || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className={classes.totalRow}>
                      <TableCell colSpan={2} align="right">
                        {i18n.t("users.salesReport.details.total")}
                      </TableCell>
                      <TableCell align="center">
                        {formatCurrency(calculateTotal())}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            ) : (
              <Box p={4} textAlign="center">
                <Typography variant="body1" color="textSecondary">
                  {i18n.t("users.salesReport.details.noSales")}
                </Typography>
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default SalesDetailsModal;
